import type { Component } from '@wix/thunderbolt-becky-types'
import { envRefs } from '@wix/thunderbolt-catharsis'
import { createCssCompNode } from '../cssCompNode'
import type { FeatureRefs } from '../cssFeatures.types'
import { isMasterPage, isPageComponent, isRefComponent } from './zIndexCompNodeHelpers'
import { getClassicZIndexValues } from './zIndexCompNodeClassicHelpers'
import { getResponsiveZIndexValues } from './zIndexCompNodeResponsiveHelpers'
import type { ZIndexCssChild, ZIndexPageChildren } from './zIndexCss.types'
import type { PageDefinition } from '@wix/thunderbolt-components-native'

const getChildren = (comp: Component, refs: FeatureRefs<'zIndexCss'>) => {
	return (comp.components || []).map(refs.defaultZIndexCss)
}

export const defaultZIndexCss = (isEditor: boolean) =>
	createCssCompNode('zIndexCss', 'defaultZIndexCss', {
		getDependencies: (component: Component, refs: FeatureRefs<'zIndexCss'>) => {
			// Checking if we are parents of a page or a master page
			if (isPageComponent(component) || isMasterPage(component)) {
				return {
					compData: refs.dataQuery<PageDefinition['data']>(component.dataQuery!),
					pageOriginalChildren: getChildren(component, refs),
					isMobileView: envRefs.isMobileView,
					hasResponsiveLayout: refs.hasResponsiveLayout(component),
				}
			}
			return {
				refCompChildren: isRefComponent(component) ? getChildren(component, refs) : null,
				compLayouts: component.layoutQuery ? refs.compLayouts(component) : null,
				pinnedStyle: refs.pinnedStyle(component),
				hasResponsiveLayout: refs.hasResponsiveLayout(component),
			}
		},
		toViewItem: (component, deps) => {
			// Checking if it's not a page container which is rendering it's children
			if (!isPageComponent(component) && !isMasterPage(component)) {
				return {
					component,
					refCompChildren: deps?.refCompChildren,
					compLayouts: deps?.compLayouts,
					pinnedStyle: !!deps?.pinnedStyle,
				} as ZIndexCssChild
			}

			// remove null / undefined children
			const pageOriginalChildren = (deps?.pageOriginalChildren?.filter(Boolean) || []) as ZIndexPageChildren

			return deps.hasResponsiveLayout
				? getResponsiveZIndexValues(component, pageOriginalChildren, isEditor)
				: getClassicZIndexValues(
					component,
					pageOriginalChildren,
					deps.isMobileView || false,
					deps.compData?.isPopup || false
				)
		},
	})
